import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, FolderOpen, CalendarDays, CheckCircle2, Camera } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg, API } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";

const FEATURES = [
  { icon: FolderOpen, t: "Drive", d: "Eure Fotos & Videos an einem Ort." },
  { icon: CalendarDays, t: "Redaktionsplan", d: "Wann was gepostet wird." },
  { icon: CheckCircle2, t: "Freigabe", d: "Beiträge per Klick freigeben, kommentieren oder sperren." },
];

const DETAILS = [
  { icon: CheckCircle2, t: "Freigabe", d: "Direkt nach dem Login seht ihr alle Videos und Fotos, die auf eure Zustimmung warten. Mit einem Tipp entscheidet ihr: Grün = freigegeben, Gelb = noch offen, Rot = gesperrt. Der farbige Rahmen zeigt sofort, was durch ist. Über „Caption & Kommentare“ lest ihr den Text zum Post und könnt uns Änderungswünsche schreiben." },
  { icon: Camera, t: "Drehtermine", d: "Wenn wir einen neuen Dreh planen, erscheint der Termin ganz oben. Ihr bestätigt ihn mit einem Klick oder schlagt einfach einen anderen Tag und eine andere Uhrzeit vor." },
  { icon: CalendarDays, t: "Redaktionsplan", d: "Im Kalender seht ihr jeden geplanten Beitrag des Monats als kleinen Zettel – mit Datum, Uhrzeit, Vorschaubild und Status. So wisst ihr immer, was wann online geht." },
  { icon: FolderOpen, t: "Drive", d: "Hier liegen alle Fotos und Videos aus euren Drehs, sortiert in Ordnern. Ihr könnt alles ansehen und direkt auf euer Handy oder euren Computer herunterladen." },
];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bg, setBg] = useState(`${API}/public/login-bg?t=${Date.now()}`);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await login(email, password); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-x-clip bg-background">
      <div className="fixed inset-0">
        <img src={bg} onError={() => setBg("/login-bg.jpg?v=3")} alt="" className="h-full w-full object-cover opacity-50" data-testid="login-background" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />
      </div>
      <div className="absolute top-4 right-4 z-20"><ThemeToggle testId="login-theme-toggle" className="glass border" /></div>
      <div className="relative z-10 mx-auto max-w-xl lg:max-w-7xl px-6 lg:px-12 pt-12 pb-16 sm:pt-20 lg:grid lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-x-16 xl:gap-x-24 lg:items-start">
        <div className="lg:col-start-1 lg:row-start-1">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-teal-400 mb-8 fade-up">CRE8 Studio · Medienagentur</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95] fade-up" data-testid="login-title">Content<br /><span className="text-teal-400">Management</span></h1>
        <div className="h-1 w-20 bg-teal-400 mt-6 mb-8" />

        <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-8 fade-up" data-testid="login-features">
          {FEATURES.map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-white/10 bg-[#33373f]/60 backdrop-blur-md p-2.5 sm:p-3 lg:p-5 min-w-0">
              <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-teal-400 mb-2" />
              <p className="text-[13px] sm:text-sm lg:text-base font-semibold break-words hyphens-auto" lang="de">{t}</p>
              <p className="text-[11px] lg:text-sm leading-snug text-slate-400 mt-0.5">{d}</p>
            </div>
          ))}
        </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#33373f]/85 backdrop-blur-xl p-6 sm:p-8 fade-up lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-20 lg:mt-24" data-testid="login-form">
          <h2 className="text-2xl font-semibold mb-1">Anmelden</h2>
          <p className="text-sm text-slate-400 mb-6">Mit deinem Zugang von der Agentur.</p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail oder Benutzername</Label>
              <Input id="email" type="text" autoCapitalize="none" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw">Passwort</Label>
              <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-12" />
            </div>
            {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold" data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
            </Button>
          </div>
        </form>

        <section className="mt-14 lg:mt-6 lg:col-start-1 lg:row-start-2" data-testid="login-intro">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-slate-400 mb-3">Wofür ist das Portal?</p>
          <p className="text-base text-slate-200 leading-relaxed mb-8">
            Hier arbeiten wir gemeinsam an euren Social-Media-Inhalten: Wir drehen Fotos und Videos, laden sie hoch und planen sie in euren
            Posting-Rhythmus ein. Ihr behaltet den Überblick und gebt jeden Beitrag frei, bevor er online geht – ganz einfach vom Handy aus.
          </p>
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4">
            {DETAILS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex gap-4 rounded-xl border border-white/10 bg-[#33373f]/60 backdrop-blur-md p-4">
                <div className="h-10 w-10 shrink-0 rounded-lg bg-teal-500/15 grid place-items-center"><Icon className="h-5 w-5 text-teal-400" /></div>
                <div><p className="font-semibold mb-1">{t}</p><p className="text-sm text-slate-400 leading-relaxed">{d}</p></div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-10 text-center lg:text-left">Zugang erhaltet ihr von eurer Agentur.</p>
        </section>
      </div>
    </div>
  );
}
